import { LogoMark } from "@/components/Logo";
import { useState } from "react";
import { useLocation } from "wouter";
import {
  ArrowRight,
  Eye,
  EyeOff,
  Lock,
  Mail,
  ShieldCheck,
  User,
  Building2,
} from "lucide-react";

type Mode = "login" | "register" | "totp";

export default function Login() {
  const [, navigate] = useLocation();
  const [mode, setMode] = useState<Mode>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(true);
  const [name, setName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [totpToken, setTotpToken] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({ error: `Erro ${res.status}` }));
        setError(data.error || `Erro ${res.status}`);
        return;
      }
      const data = await res.json();
      if (data.requiresTOTP) {
        setTotpToken(data.totpToken);
        setMode("totp");
        return;
      }
      if (data.user?.isSuperAdmin && !data.user?.tenantId) {
        window.location.href = "/super-admin";
      } else if (data.user?.tenantId) {
        window.location.href = "/";
      } else {
        window.location.href = "/onboarding";
      }
    } catch (err) {
      // Mensagem amigável já vai pro setError; log no console pra dev tools
      // e Sentry — antes esse catch ficava silencioso e debugar problema de
      // rede no login era impossível sem reproduzir localmente.
      console.error("[Login] Network/fetch error:", err);
      setError("Erro de conexão com o servidor. Verifique sua internet.");
    } finally {
      setLoading(false);
    }
  };

  const handleTOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch("/api/auth/verify-totp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ totpToken, totpCode }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({ error: `Erro ${res.status}` }));
        setError(data.error || `Erro ${res.status}`);
        return;
      }
      window.location.href = "/";
    } catch (err) {
      // Mensagem amigável já vai pro setError; log no console pra dev tools
      // e Sentry — antes esse catch ficava silencioso e debugar problema de
      // rede no login era impossível sem reproduzir localmente.
      console.error("[Login] Network/fetch error:", err);
      setError("Erro de conexão com o servidor. Verifique sua internet.");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ companyName, name, email, password }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({ error: `Erro ${res.status}` }));
        setError(data.error || `Erro ${res.status}`);
        return;
      }
      window.location.href = "/onboarding";
    } catch (err) {
      // Mensagem amigável já vai pro setError; log no console pra dev tools
      // e Sentry — antes esse catch ficava silencioso e debugar problema de
      // rede no login era impossível sem reproduzir localmente.
      console.error("[Login] Network/fetch error:", err);
      setError("Erro de conexão com o servidor. Verifique sua internet.");
    } finally {
      setLoading(false);
    }
  };

  const eyebrow =
    mode === "login"
      ? "Entrar na plataforma"
      : mode === "register"
        ? "Nova conta"
        : "Verificação em 2 passos";
  const title =
    mode === "login"
      ? "Bem-vindo de volta."
      : mode === "register"
        ? "Vamos começar."
        : "Digite o código.";
  const sub =
    mode === "login"
      ? "Acesse sua conta para continuar as conversas."
      : mode === "register"
        ? "Crie sua conta em menos de 1 minuto — sem cartão."
        : "Use seu app autenticador pra gerar o código de 6 dígitos.";

  return (
    <div
      className="min-h-screen grid grid-cols-1 md:grid-cols-[1.05fr_1fr]"
      style={{ background: "var(--brand-bone)" }}
    >
      {/* ─── Left: form side ─── */}
      <div
        className="flex flex-col px-6 py-10 md:px-14 md:py-11 relative"
        style={{ background: "var(--brand-bone)", color: "var(--brand-ink)" }}
      >
        <a
          href="/"
          className="flex items-center gap-2.5 self-start"
          style={{ color: "var(--brand-teal)" }}
        >
          <LogoMark size={28} />
          <span
            className="font-display font-semibold text-[17px] tracking-[-0.02em]"
            style={{ color: "var(--brand-ink)" }}
          >
            Lead{" "}
            <span className="font-normal" style={{ color: "var(--brand-fog)" }}>
              Conversa
            </span>
          </span>
        </a>

        <div className="flex-1 flex items-center justify-center">
          <div className="w-full max-w-[380px] flex flex-col gap-6">
            <div>
              <div
                className="font-mono text-[10px] tracking-[0.2em] uppercase"
                style={{ color: "var(--brand-fog)" }}
              >
                {eyebrow}
              </div>
              <h1 className="font-display font-semibold text-[32px] tracking-[-0.03em] leading-[1.05] mt-2">
                {title}
              </h1>
              <p className="text-[14px] mt-1.5" style={{ color: "var(--brand-fog)" }}>
                {sub}
              </p>
            </div>

            {mode === "login" && (
              <form onSubmit={handleLogin} className="flex flex-col gap-5">
                <Field label="E-mail" htmlFor="email">
                  <InputWrap icon={<Mail className="h-3.5 w-3.5" />}>
                    <input
                      id="email"
                      type="email"
                      placeholder="voce@empresa.com.br"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={inputCls}
                    />
                  </InputWrap>
                </Field>

                <Field
                  label="Senha"
                  htmlFor="pw"
                  right={
                    <button
                      type="button"
                      onClick={() =>
                        setError("Em breve você poderá recuperar sua senha por e-mail.")
                      }
                      className="font-medium hover:underline"
                      style={{ color: "var(--brand-teal)" }}
                    >
                      Esqueci minha senha
                    </button>
                  }
                >
                  <InputWrap icon={<Lock className="h-3.5 w-3.5" />}>
                    <input
                      id="pw"
                      type={showPw ? "text" : "password"}
                      placeholder="••••••••"
                      autoComplete="current-password"
                      required
                      minLength={8}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className={inputCls}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw((v) => !v)}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-[var(--brand-mist)] transition-colors"
                      style={{ color: "var(--brand-fog)" }}
                      tabIndex={-1}
                    >
                      {showPw ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                    </button>
                  </InputWrap>
                </Field>

                <label className="flex items-center gap-2 text-[12px] cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                    className="h-4 w-4 rounded accent-[var(--brand-teal)] cursor-pointer"
                  />
                  Manter conectado
                </label>

                {error && <ErrorMsg>{error}</ErrorMsg>}

                <PrimaryBtn loading={loading}>
                  Entrar
                  <ArrowRight className="h-3.5 w-3.5" />
                </PrimaryBtn>

                <FooterLink
                  question="Não tem uma conta?"
                  label="Criar conta grátis"
                  onClick={() => {
                    setMode("register");
                    setError("");
                  }}
                />
              </form>
            )}

            {mode === "register" && (
              <form onSubmit={handleRegister} className="flex flex-col gap-5">
                <Field label="Nome da empresa" htmlFor="company">
                  <InputWrap icon={<Building2 className="h-3.5 w-3.5" />}>
                    <input
                      id="company"
                      type="text"
                      placeholder="Minha Empresa"
                      required
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      className={inputCls}
                    />
                  </InputWrap>
                </Field>

                <Field label="Seu nome" htmlFor="name">
                  <InputWrap icon={<User className="h-3.5 w-3.5" />}>
                    <input
                      id="name"
                      type="text"
                      placeholder="João Silva"
                      autoComplete="name"
                      required
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className={inputCls}
                    />
                  </InputWrap>
                </Field>

                <Field label="E-mail" htmlFor="email-r">
                  <InputWrap icon={<Mail className="h-3.5 w-3.5" />}>
                    <input
                      id="email-r"
                      type="email"
                      placeholder="voce@empresa.com.br"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={inputCls}
                    />
                  </InputWrap>
                </Field>

                <Field label="Senha" htmlFor="pw-r">
                  <InputWrap icon={<Lock className="h-3.5 w-3.5" />}>
                    <input
                      id="pw-r"
                      type={showPw ? "text" : "password"}
                      placeholder="Mínimo 8 caracteres"
                      autoComplete="new-password"
                      required
                      minLength={8}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className={inputCls}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw((v) => !v)}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-[var(--brand-mist)] transition-colors"
                      style={{ color: "var(--brand-fog)" }}
                      tabIndex={-1}
                    >
                      {showPw ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                    </button>
                  </InputWrap>
                </Field>

                {error && <ErrorMsg>{error}</ErrorMsg>}

                <PrimaryBtn loading={loading}>
                  Criar conta
                  <ArrowRight className="h-3.5 w-3.5" />
                </PrimaryBtn>

                <FooterLink
                  question="Já tem uma conta?"
                  label="Fazer login"
                  onClick={() => {
                    setMode("login");
                    setError("");
                  }}
                />
              </form>
            )}

            {mode === "totp" && (
              <form onSubmit={handleTOTP} className="flex flex-col gap-5">
                <Field label="Código de verificação" htmlFor="totp">
                  <InputWrap icon={<ShieldCheck className="h-3.5 w-3.5" />}>
                    <input
                      id="totp"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]{6}"
                      maxLength={6}
                      placeholder="000000"
                      required
                      autoFocus
                      autoComplete="one-time-code"
                      value={totpCode}
                      onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))}
                      className={`${inputCls} text-center tracking-[0.4em] font-mono text-lg`}
                    />
                  </InputWrap>
                </Field>

                {error && <ErrorMsg>{error}</ErrorMsg>}

                <PrimaryBtn loading={loading}>
                  Verificar
                  <ArrowRight className="h-3.5 w-3.5" />
                </PrimaryBtn>

                <button
                  type="button"
                  className="text-[13px] text-center hover:underline"
                  style={{ color: "var(--brand-fog)" }}
                  onClick={() => {
                    setMode("login");
                    setTotpCode("");
                    setTotpToken("");
                    setError("");
                  }}
                >
                  Voltar ao login
                </button>
              </form>
            )}
          </div>
        </div>

        <div
          className="flex justify-between font-mono text-[11px] tracking-[0.05em]"
          style={{ color: "var(--brand-fog)" }}
        >
          <span>© {new Date().getFullYear()} Lead Conversa</span>
          <span className="hidden sm:block">
            <a href="#" className="hover:text-[var(--brand-ink)]">
              Termos
            </a>{" "}
            ·{" "}
            <a href="#" className="hover:text-[var(--brand-ink)]">
              Privacidade
            </a>{" "}
            ·{" "}
            <a href="#" className="hover:text-[var(--brand-ink)]">
              Ajuda
            </a>
          </span>
        </div>
      </div>

      {/* ─── Right: brand panel ─── */}
      <div
        className="hidden md:flex flex-col justify-between px-14 py-11 relative overflow-hidden"
        style={{ background: "var(--brand-teal)", color: "var(--brand-bone)" }}
      >
        <div
          className="absolute pointer-events-none opacity-10"
          style={{ right: -140, bottom: -140 }}
        >
          <LogoMark size={480} primary="var(--brand-bone)" accent="var(--brand-accent)" />
        </div>

        <div className="relative flex justify-between items-center gap-4">
          <div
            className="font-mono text-[10px] tracking-[0.2em] uppercase opacity-70"
          >
            Plataforma de conversação
          </div>
          <LivePill>248 conversas ativas agora</LivePill>
        </div>

        <div className="relative">
          <h2 className="font-serif font-medium text-[44px] leading-[1.05] tracking-[-0.02em] max-w-[18ch] m-0">
            Cada{" "}
            <em className="italic" style={{ color: "var(--brand-accent)" }}>
              conversa
            </em>{" "}
            é um lead esperando resposta.
          </h2>
        </div>

        <div
          className="relative pt-5 flex flex-col gap-3.5 max-w-[420px]"
          style={{ borderTop: "1px solid rgba(255,255,255,0.15)" }}
        >
          <div className="font-serif text-[18px] leading-[1.45] tracking-[-0.01em]">
            "Trocamos planilha e 3 ferramentas por uma só. Nosso tempo de primeira resposta caiu de 2h para 4 minutos."
          </div>
          <div className="flex items-center gap-3">
            <div
              className="h-[38px] w-[38px] rounded-full flex items-center justify-center font-semibold text-[14px] flex-none"
              style={{ background: "var(--brand-accent)", color: "var(--brand-teal-deep)" }}
            >
              MR
            </div>
            <div>
              <div className="text-[13px] font-medium">Mariana Ribeiro</div>
              <div
                className="font-mono text-[11px] tracking-[0.05em] uppercase opacity-60"
              >
                Head de Growth · Paradigma
              </div>
            </div>
          </div>
        </div>

        <div
          className="relative grid grid-cols-3 gap-[18px] pt-5"
          style={{ borderTop: "1px solid rgba(255,255,255,0.15)" }}
        >
          <BrandStat n="1.2M+" l="Mensagens/mês" />
          <BrandStat n="68%" l="Taxa resposta" />
          <BrandStat n="3.400" l="Empresas" />
        </div>
      </div>
    </div>
  );
}

// ─── Parts ──────────────────────────────────────────────────────────

const inputCls =
  "w-full pl-10 pr-3.5 py-3 border rounded-lg bg-white text-[14px] transition-[border-color,box-shadow] outline-none focus:border-[color:var(--brand-teal)] focus:shadow-[0_0_0_3px_color-mix(in_oklab,var(--brand-teal)_18%,transparent)] placeholder:opacity-70";

function Field({
  label,
  htmlFor,
  right,
  children,
}: {
  label: string;
  htmlFor: string;
  right?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label
        htmlFor={htmlFor}
        className="text-[12px] font-medium flex justify-between items-center"
        style={{ color: "var(--brand-ink)" }}
      >
        <span>{label}</span>
        {right}
      </label>
      {children}
    </div>
  );
}

function InputWrap({
  icon,
  children,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="relative">
      <span
        className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
        style={{ color: "var(--brand-fog)" }}
      >
        {icon}
      </span>
      {children}
    </div>
  );
}

function PrimaryBtn({
  loading,
  children,
}: {
  loading?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="submit"
      disabled={loading}
      className="w-full font-display font-medium text-[14px] tracking-[-0.01em] py-3 px-4 rounded-lg transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60"
      style={{ background: "var(--brand-teal)", color: "var(--brand-bone)" }}
      onMouseEnter={(e) =>
        (e.currentTarget.style.background = "var(--brand-teal-deep)")
      }
      onMouseLeave={(e) => (e.currentTarget.style.background = "var(--brand-teal)")}
    >
      {loading ? "Aguarde…" : children}
    </button>
  );
}

function FooterLink({
  question,
  label,
  onClick,
}: {
  question: string;
  label: string;
  onClick: () => void;
}) {
  return (
    <div className="text-[13px] text-center" style={{ color: "var(--brand-fog)" }}>
      {question}{" "}
      <button
        type="button"
        onClick={onClick}
        className="font-medium hover:underline"
        style={{ color: "var(--brand-teal)" }}
      >
        {label}
      </button>
    </div>
  );
}

function ErrorMsg({ children }: { children: React.ReactNode }) {
  return (
    <p
      className="text-[13px] rounded-md px-3 py-2 border"
      style={{
        color: "oklch(0.45 0.18 25)",
        borderColor: "color-mix(in oklab, oklch(0.60 0.18 25) 25%, transparent)",
        background: "color-mix(in oklab, oklch(0.60 0.18 25) 8%, var(--brand-bone))",
      }}
    >
      {children}
    </p>
  );
}

function LivePill({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full font-mono text-[10px] tracking-[0.1em] uppercase whitespace-nowrap"
      style={{
        background: "rgba(250,250,247,0.1)",
        backdropFilter: "blur(6px)",
      }}
    >
      <span
        className="w-1.5 h-1.5 rounded-full animate-pulse"
        style={{
          background: "var(--brand-accent)",
          boxShadow: "0 0 0 3px color-mix(in oklab, var(--brand-accent) 30%, transparent)",
        }}
      />
      {children}
    </div>
  );
}

function BrandStat({ n, l }: { n: string; l: string }) {
  return (
    <div className="flex flex-col gap-[3px]">
      <div className="font-display font-semibold text-[28px] tracking-[-0.025em] leading-none">
        {n}
      </div>
      <div
        className="font-mono text-[10px] tracking-[0.1em] uppercase opacity-65"
      >
        {l}
      </div>
    </div>
  );
}
